import * as React from 'react'
import cn from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation } from '@remix-run/react'
import { RiUserLine, RiSearch2Line, RiListUnordered, RiHome2Line, RiPhoneLine, RiMailSendLine } from 'react-icons/ri'
import { MdOutlineSolarPower } from 'react-icons/md'
import { TbShoppingBag } from 'react-icons/tb'
import { BiMessageRounded } from 'react-icons/bi'
import { Container, Heading, Separator, Text } from '@radix-ui/themes'

import { appView, ROOT_I18NEXT } from '~/routes/_layout/root.component'
import type { App } from '~/routes/_layout/root.component'
import type { GetOrder } from '~/models/ecommerce-providers/sylius/shop/fetch/order/get-order'
import type { Locale } from '~/models/locale'
import { Image } from '~/components/ui/image'
import { Search } from '~/routes/_layout/search'
import { appConfig } from '~/app-config.ts'
import { IconType } from 'react-icons'


type NavbarProps = {
  cart: GetOrder
  locale: Locale
  loggedIn: boolean
  setApp: React.Dispatch<React.SetStateAction<App>>
}

enum DISPLAY {
  navbar = 'navbar',
  sidebar = 'sidebar',
}

type MenuItem = {
  label: string
  path: string
  Icon: IconType
  className?: string
  callback?: (props: any) => void
  display: DISPLAY[]
}

const menu: MenuItem[] = [
  {
    label: 'home',
    path: '',
    Icon: RiHome2Line,
    className: 'hidden',
    callback: () => {},
    display: [DISPLAY.sidebar],
  }, {
    label: 'products',
    path: '/products',
    Icon: MdOutlineSolarPower,
    className: 'hidden xs:flex',
    callback: () => {},
    display: [DISPLAY.navbar, DISPLAY.sidebar],
  }, {
    label: 'search',
    path: '',
    Icon: RiSearch2Line,
    className: 'hidden xs:flex',
    callback: ({ e, setApp }: { e: React.MouseEvent<HTMLElement>, setApp: React.Dispatch<React.SetStateAction<App>> }) => {
      e.preventDefault()
    },
    display: [DISPLAY.navbar],
  }, {
    label: 'cart',
    path: '/cart',
    Icon: TbShoppingBag,
    className: 'hidden xs:flex',
    callback: ({ e, setApp }: { e: React.MouseEvent<HTMLElement>, setApp: React.Dispatch<React.SetStateAction<App>> }) => {
      e.preventDefault()
      setApp((prev) => ({
        ...prev,
        sidebarView: appView.cartView,
        displaySidebar: true,
        displayModal: false,
      }))
    },
    display: [DISPLAY.navbar, DISPLAY.sidebar],
  }, {
    label: 'profile',
    path: '/profile',
    Icon: RiUserLine,
    className: 'hidden xs:flex',
    callback: ({ e, setApp, loggedIn }: { e: React.MouseEvent<HTMLElement>, setApp: React.Dispatch<React.SetStateAction<App>>, loggedIn: boolean }) => {
      if (loggedIn) {
        return
      }
      e.preventDefault()
      setApp((prev) => ({
        ...prev,
        modalView: appView.loginView,
        displaySidebar: false,
        displayModal: true,
      }))
    },
    display: [DISPLAY.navbar, DISPLAY.sidebar],
  }, {
    label: 'menu',
    path: '#footer',
    Icon: RiListUnordered,
    className: 'flex xs:hidden',
    callback: ({ e, setApp }: { e: React.MouseEvent<HTMLElement>, setApp: React.Dispatch<React.SetStateAction<App>> }) => {
      e.preventDefault()
      setApp((prev) => ({
        ...prev,
        sidebarView: appView.menuView,
        displaySidebar: true,
        displayModal: false,
      }))
    },
    display: [DISPLAY.navbar],
  }, {
    label: 'contact',
    path: '/contact',
    Icon: BiMessageRounded,
    className: 'hidden xs:flex',
    callback: () => {},
    display: [DISPLAY.sidebar],
  },
]

const hasHeader = ({ locale } : { locale: Locale }) => [
  `/${locale}`, `/${locale}/contact`
]

const Navbar: React.FC<NavbarProps> = ({ cart, locale, loggedIn, setApp }) => {
  const { pathname } = useLocation()
  const { t } = useTranslation(ROOT_I18NEXT.routeRoot)
  const [isScroll, setIsScroll] = React.useState(false)

  React.useEffect(() => {
    window.addEventListener('scroll', () => setIsScroll((window.scrollY ?? document.documentElement.scrollTop) > 100))
  }, [locale, pathname])

  return (
    <div className="fixed z-40 w-full shadow">
      <div className="app-Header-bg">
        <Container px="5">
          <div className="hidden sm:grid grid-cols-8 justify-center -gap-y-1 py-1 md:py-0">
            <div className="col-span-4 lg:col-span-3 py-2 md:pr-6 text-gray-2 flex justify-center md:justify-start items-center gap-x-2">
              <RiPhoneLine size="1.4em" className="text-accent-9" />
              <Heading as="h6" size="2" weight="light" color="indigo">
                {t('label.clientServices')}
              </Heading>
              <a href={`tel:${appConfig.legal.phone}`} className="text-sm italic text-accent-9">
                {appConfig.legal.phone_label}
              </a>
            </div>
            <div className="col-span-4 lg:col-span-5 py-2 flex justify-center md:justify-end text-gray-3 items-center gap-x-4">
              <div className="flex items-center gap-x-2">
                <RiMailSendLine size="1.4em" className="text-accent-9" />
                <Heading as="h6" size="2" weight="light" color="indigo">
                  {t('label.email')}
                </Heading>
                <a href={`mailto:${appConfig.legal.email}`} className="text-sm italic text-accent-9">
                  {appConfig.legal.email}
                </a>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <div className="py-1 transition duration-300 ease-in-out bg-accent-1">
        <Container px="5">
          <div className="flex items-center justify-between py-1 gap-y-4">
            <Link to={`/${locale}`} className="inline-flex flex-col gap-y-2 items-center">
              <Image api={false} alt="Logo" src="/pwa/img/compress/logo/logo.webp" width={120} />
            </Link>
            <nav className="flex items-center gap-x-8">
              {menu.map(({ label, path, Icon, className, callback = undefined }) => label !== 'search' ? (
                <NavLink
                  key={label + path}
                  end
                  prefetch="intent"
                  to={`/${locale}${path}`}
                  className={({ isActive }) => cn('flex flex-col gap-y-0.5 items-center',
                    'font-bold whitespace-nowrap',
                    'transition duration-300 ease-in-out',
                    'hover:text-accent-8', {
                      'text-accent-10': isActive,
                      'text-accent-9': !isActive,
                    }, className)}
                  onClick={(e) => !!callback && callback({ e, setApp, loggedIn })}
                >
                  <Icon size="1.6em" />
                  <Text size="1" weight="light">
                    {t(`label.${label}`)}
                  </Text>
                </NavLink>
              ) : (
                <Search key={label+path} locale={locale} path={path} className={className} />
              ))}
            </nav>
          </div>
        </Container>
      </div>
    </div>
  )
}
Navbar.displayName = 'Navbar'


export {
  Navbar,
  menu,
  hasHeader,
  type MenuItem as NavbarMenuItem,
  DISPLAY as NAVBAR_DISPLAY,
}
